import React, { useState, useEffect, useRef } from "react";
import axiosInstance from "../axiosConfig";
import "./ChatComponent.css";

const Chat = ({ userId }) => {
  const [activeTab, setActiveTab] = useState("general");
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [receiverUsername, setReceiverUsername] = useState("");
  const [receiverId, setReceiverId] = useState(null);
  const [selectedPlayRequestId, setSelectedPlayRequestId] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);

  // Chat lists
  const [generalChats, setGeneralChats] = useState([]);
  const [memberPortalChats, setMemberPortalChats] = useState([]);
  const [allUsers, setAllUsers] = useState([]);

  const [userListOpen, setUserListOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const messagesEndRef = useRef(null);
  const chatAreaRef = useRef(null);

  // --- Helpers ---------------------------------------------------------------

  // Robust equality between ids that might be numbers or strings
  const idsEqual = (a, b) =>
    a != null && b != null && String(a) === String(b);

  // Normalize a member-portal message into the general shape your UI expects
  const normalizeMemberPortalMessage = (msg) => {
    // Try common shapes from APIs:
    //   sender_id, sender.id, sender (direct id), sender_name/sender_username
    const senderId =
      msg.sender_id ??
      (msg.sender && msg.sender.id) ??
      msg.sender ??
      null;

    const isMine = idsEqual(senderId, currentUserId);

    const usernameCandidate =
      msg.sender_name ??
      msg.sender_username ??
      (isMine ? "You" : "Member");

    return {
      sender: { username: usernameCandidate },
      content: msg.content ?? "",
      timestamp: msg.timestamp ?? msg.created_at ?? new Date().toISOString(),
      is_sent_by_user: !!isMine,
    };
  };

  const getDisplayName = (m) => {
  if (!m || m.sender == null) return "User";
  return typeof m.sender === "string"
    ? m.sender
    : (m.sender.username || m.sender.name || "User");
};

  // --- Current user ----------------------------------------------------------

  useEffect(() => {
    (async () => {
      try {
        const res = await axiosInstance.get("/auth/current-user/");
        const id = res?.data?.id || res?.data?.user?.id || null;
        setCurrentUserId(id);
      } catch (e) {
        setCurrentUserId(null);
      }
    })();
  }, []);

  // --- Threads for both tabs -------------------------------------------------

  const fetchConversationThreads = async () => {
    try {
      const response = await axiosInstance.get("/chats/get_users_with_history/");
      setGeneralChats(response.data.activeChats || []);
      setAllUsers(response.data.allUsers || []);

      if (activeTab === "member-portal") {
        try {
          const memberPortalResponse = await axiosInstance.get(
            "/member-portal/play-requests/"
          );
          const requestsWithActivity = (memberPortalResponse.data || []).filter(
            (req) => !["declined", "cancelled"].includes(req.status)
          );
          setMemberPortalChats(requestsWithActivity);
        } catch (error) {
          console.error("Error fetching member portal chats:", error);
        }
      }
    } catch (error) {
      console.error("Error fetching conversation threads:", error);
    }
  };

  useEffect(() => {
    fetchConversationThreads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // --- Messages fetching -----------------------------------------------------

  useEffect(() => {
    if (activeTab === "general" && receiverId) {
      fetchGeneralMessages();
    } else if (activeTab === "member-portal" && selectedPlayRequestId) {
      fetchMemberPortalMessages();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [receiverId, selectedPlayRequestId, activeTab, currentUserId]);

  const fetchGeneralMessages = async () => {
    if (!receiverId) return;
    setIsLoading(true);
    try {
      const response = await axiosInstance.get(
        `/chats/get_messages/${receiverId}/`
      );
      // Assume backend already returns in correct shape for general
      setMessages(response.data || []);
    } catch (error) {
      console.error("Error fetching general messages:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchMemberPortalMessages = async () => {
    if (!selectedPlayRequestId) return;
    setIsLoading(true);
    try {
      const response = await axiosInstance.get(
        `/member-portal/chats/${selectedPlayRequestId}/`
      );

      const raw = response?.data?.messages || [];
      const formatted = raw.map(normalizeMemberPortalMessage);
      setMessages(formatted);
    } catch (error) {
      console.error("Error fetching member portal messages:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // --- Send ------------------------------------------------------------------

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    try {
      if (activeTab === "general" && receiverId) {
        await axiosInstance.post("/chats/send_chat/", {
          receiverId: receiverId,
          content: newMessage,
        });

        setMessages((prev) => [
          ...prev,
          {
            sender: { username: "You" },
            content: newMessage,
            timestamp: new Date(),
            is_sent_by_user: true,
          },
        ]);
      } else if (activeTab === "member-portal" && selectedPlayRequestId) {
        await axiosInstance.post(
          `/member-portal/chats/${selectedPlayRequestId}/send/`,
          { content: newMessage }
        );

        setMessages((prev) => [
          ...prev,
          {
            sender: { username: "You" },
            content: newMessage,
            timestamp: new Date(),
            is_sent_by_user: true,
          },
        ]);
      }

      setNewMessage("");
    } catch (err) {
      console.error("Error sending message", err);
    }
  };

  // --- UX helpers ------------------------------------------------------------

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  const formatTimestamp = (timestamp) =>
    new Date(timestamp).toLocaleTimeString([], {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

  const handleGeneralUserSelect = (user) => {
    setReceiverUsername(user.username);
    setReceiverId(user.id);
    setSelectedPlayRequestId(null);
    setUserListOpen(false);
    setSearchQuery("");
  };

  const handleMemberPortalChatSelect = (request) => {
    const otherUser = request.host?.user || request.requester?.user;
    const otherUserName = otherUser?.first_name || otherUser?.username || "Member";

    setReceiverUsername(`${otherUserName} - ${request.target_club?.name}`);
    setReceiverId(otherUser?.id || null); // not required to send, but harmless
    setSelectedPlayRequestId(request.request_id);
    setUserListOpen(false);
    setSearchQuery("");
  };

  const getCurrentChatList = () => {
    const searchLower = searchQuery.toLowerCase();

    if (activeTab === "general") {
      const filtered = (generalChats || []).filter((chat) =>
        (chat.username || "").toLowerCase().includes(searchLower)
      );
      return { chats: filtered, showAllUsers: true };
    } else {
      const filtered = (memberPortalChats || []).filter((request) => {
        const clubName = request.target_club?.name || "";
        const otherUser = request.host?.user || request.requester?.user;
        const userName = otherUser?.first_name || otherUser?.username || "";
        return (
          userName.toLowerCase().includes(searchLower) ||
          clubName.toLowerCase().includes(searchLower)
        );
      });
      return { chats: filtered, showAllUsers: false };
    }
  };

  const { chats: currentChats, showAllUsers } = getCurrentChatList();

  const filteredAllUsers = showAllUsers
    ? (allUsers || []).filter((user) =>
        (user.username || "").toLowerCase().includes(searchQuery.toLowerCase())
      )
    : [];

  const getCurrentChatTitle = () => {
    if (activeTab === "general") {
      return receiverUsername
        ? `Chat with ${receiverUsername}`
        : "Select a user to start chatting";
    } else {
      return receiverUsername || "Select a member portal conversation";
    }
  };

  // --- Render ----------------------------------------------------------------

  return (
    <div className="chat-component">
      {/* Tabs */}
      <div className="chat-tab-container">
        <button
          className={`chat-tab-button ${
            activeTab === "general" ? "chat-tab-active" : ""
          }`}
          onClick={() => {
            setActiveTab("general");
            setMessages([]);
            setReceiverId(null);
            setSelectedPlayRequestId(null);
            setReceiverUsername("");
          }}
        >
          General
        </button>
        <button
          className={`chat-tab-button ${
            activeTab === "member-portal" ? "chat-tab-active" : ""
          }`}
          onClick={() => {
            setActiveTab("member-portal");
            setMessages([]);
            setReceiverId(null);
            setSelectedPlayRequestId(null);
            setReceiverUsername("");
          }}
        >
          Member Portal
        </button>
      </div>

      {/* Chat selector */}
      <div className="chat-selector">
        <button
          className="chat-selector-button"
          onClick={() => setUserListOpen(!userListOpen)}
        >
          {getCurrentChatTitle()}
          <span className="chat-dropdown-arrow">
            {userListOpen ? "▲" : "▼"}
          </span>
        </button>
      </div>

      {/* User / Chat List */}
      {userListOpen && (
        <div className="chat-user-list">
          <div className="chat-search-container">
            <input
              type="text"
              placeholder={`Search ${
                activeTab === "general" ? "users" : "conversations"
              }...`}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="chat-search-input"
            />
          </div>

          {activeTab === "general" ? (
            <>
              {currentChats.length > 0 && (
                <div className="chat-section">
                  <div className="chat-section-header">Recent Conversations</div>
                  {currentChats.map((user) => (
                    <div
                      key={user.id}
                      onClick={() => handleGeneralUserSelect(user)}
                      className="chat-user-item chat-active-user"
                    >
                      {user.username}
                    </div>
                  ))}
                </div>
              )}

              <div className="chat-section">
                <div className="chat-section-header">All Users</div>
                {filteredAllUsers.map((user) => (
                  <div
                    key={user.id}
                    onClick={() => handleGeneralUserSelect(user)}
                    className="chat-user-item"
                  >
                    {user.username}
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="chat-section">
              <div className="chat-section-header">Golf Round Conversations</div>
              {currentChats.length === 0 ? (
                <div className="chat-no-conversations">
                  <p>No member portal conversations yet.</p>
                </div>
              ) : (
                currentChats.map((request) => {
                  const isCurrentUserHost =
                    currentUserId && idsEqual(request.host?.user?.id, currentUserId);
                  const isCurrentUserRequester =
                    currentUserId &&
                    idsEqual(request.requester?.user?.id, currentUserId);

                  let otherUser, userRole;
                  if (isCurrentUserHost) {
                    otherUser = request.requester?.user;
                    userRole = "host";
                  } else if (isCurrentUserRequester) {
                    otherUser = request.host?.user;
                    userRole = "guest";
                  } else {
                    otherUser = request.host?.user || request.requester?.user;
                    userRole = "unknown";
                  }

                  const otherUserName =
                    otherUser?.first_name || otherUser?.username || "Member";

                  return (
                    <div
                      key={request.request_id}
                      onClick={() => handleMemberPortalChatSelect(request)}
                      className="chat-user-item chat-portal-item"
                    >
                      <div className="chat-portal-preview">
                        <div className="chat-portal-header">
                          <span className="chat-portal-username">{otherUserName}</span>
                          <span className="chat-portal-role">{userRole}</span>
                        </div>
                        <div className="chat-portal-club">
                          {request.target_club?.name}
                        </div>
                        <div className="chat-portal-status">
                          Status: {request.status}
                        </div>
                        {request.scheduled_date && (
                          <div className="chat-portal-date">
                            {new Date(request.scheduled_date).toLocaleDateString()}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>
      )}

      {/* Chat Area */}
      <div className="chat-area" ref={chatAreaRef}>
        {isLoading ? (
          <div className="loading-spinner">
            <div></div>
          </div>
        ) : (
          <div className="chat-messages">
            {messages.length === 0 ? (
              <div className="no-messages">
                {activeTab === "general"
                  ? "No messages yet. Start a conversation!"
                  : "No messages in this golf round conversation yet."}
              </div>
            ) : (
              messages.map((message, index) => (
                <div
                  key={index}
                  className={`chat ${
                    message.is_sent_by_user ? "chat-end" : "chat-start"
                  }`}
                >
                  <div className="chat-meta">
                    <div className="chat-username">{getDisplayName(message)}</div>
                    <time className="chat-timestamp">{formatTimestamp(message.timestamp)}</time>
                  </div>
                  <div className="chat-bubble">{message.content}</div>
                  <div className="chat-footer">
                    {message.is_sent_by_user ? "Delivered" : "Seen"}
                  </div>
                </div>
              ))
            )}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {/* Message Input */}
      <form onSubmit={handleSendMessage} className="message-form">
        <input
          type="text"
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          placeholder={
            activeTab === "general"
              ? "Type a message..."
              : "Type a message about this golf round..."
          }
          disabled={
            (activeTab === "general" && !receiverId) ||
            (activeTab === "member-portal" && !selectedPlayRequestId)
          }
        />
        <button
          type="submit"
          disabled={
            !newMessage.trim() ||
            (activeTab === "general" && !receiverId) ||
            (activeTab === "member-portal" && !selectedPlayRequestId)
          }
        >
          Send
        </button>
      </form>
    </div>
  );
};

export default Chat;
